import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { constants } from "@qgiv/core-js";
import {
    getDisplayShippingInfo,
    getPaymentDetailsAdditionalText,
    reduxRegistrationsHasCommitments,
} from "@qgiv/p2p-registration";
import { useFormikContext } from "formik";
import { selectIsCms } from "../../redux/slices/configSlice";
import { selectShouldDisplayApplePay } from "../../redux/slices/appSettingsSlice";
import {
    selectAllRegistration,
    selectRegistrationTotals,
} from "../../redux/slices/registrationSlice";
import { selectCmsControlByIdByPage } from "../../redux/slices/cmsSettingsSlice";
import { selectAllP2pSettings } from "../../redux/slices/p2pSettingsSlice";
import { selectOfferShipping } from "../../redux/slices/productSettingsSlice";
import { selectAllProducts } from "../../redux/slices/productsSlice";
// eslint-disable-next-line import/no-cycle
import PaymentContentControl from "./PaymentContentControl";

const ConnectedPaymentContentControl = ({ controlId }) => {
    const {
        ENUMS: { DefaultView },
    } = constants;
    const formik = useFormikContext();

    const isCms = useSelector(selectIsCms);
    const shouldDisplayApplePay = useSelector(selectShouldDisplayApplePay);
    const registration = useSelector(selectAllRegistration);
    const control = useSelector((state) =>
        selectCmsControlByIdByPage(
            controlId,
            DefaultView.REGISTRATION_CHECKOUT_STEP,
            state,
        ),
    );
    const { storeCart = [], registrations = [] } = registration;

    const p2pSettings = useSelector(selectAllP2pSettings);
    const { enableOptionalDonation } = p2pSettings;

    const offerShipping = useSelector(selectOfferShipping);
    const products = useSelector(selectAllProducts);

    const displayOptionalDonation = enableOptionalDonation;

    const displayShippingInfo = getDisplayShippingInfo(
        products,
        storeCart,
        offerShipping,
    );

    const { total = 0 } = useSelector(selectRegistrationTotals);

    const {
        options: {
            general: {
                sectionHeading = "Payment Details",
                additionalText = "How would you like to pay?",
                commitmentAdditionalTextWhenNoTotal = "This payment method will be securely stored and will be charged if the fundraising commitment is not met. You will not be charged today!",
                commitmentAdditionalTextWhenTotal = "This payment method will be securely stored and will be charged if the fundraising commitment is not met. Today you will only be charged for the total reflected in your summary.",
            },
        },
    } = control;

    const textOptions = {
        additionalText,
        commitmentAdditionalTextWhenNoTotal,
        commitmentAdditionalTextWhenTotal,
    };

    const hasCommitments = reduxRegistrationsHasCommitments(registrations);

    const paymentDetailsAdditionalText = getPaymentDetailsAdditionalText(
        textOptions,
        hasCommitments,
        total,
    );

    const displayPayment = total > 0 || isCms || hasCommitments;

    // If the user has a non $0 registration, proceeds to the payment page,
    // applies a promo code and triggers a $0 registration let the user know
    // that they don't owe anything and can complete registration
    const displayDontOweAnythingMessage =
        !displayPayment &&
        !displayOptionalDonation &&
        !displayShippingInfo &&
        !hasCommitments;

    const paymentContentControlProps = {
        displayDontOweAnythingMessage,
        formik,
        sectionHeading,
        paymentDetailsAdditionalText,
        controlId,
        displayPayment,
        shouldDisplayApplePay,
        isCms,
    };

    return <PaymentContentControl {...paymentContentControlProps} />;
};

ConnectedPaymentContentControl.propTypes = {
    controlId: PropTypes.number.isRequired,
};

export default ConnectedPaymentContentControl;
